import styled, { css } from 'styled-components';
import React, { useState } from 'react';
import {
  Checkbox,
  color,
  Field,
  FieldStatus,
  Grid,
  GridCol,
  GridRow,
  ImportantIcon,
  Input,
  RichTextEditor,
  typeStyle,
  units
} from '@m/alchemy-ui';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { IChallenge } from '../../../common/interfaces';
import { useFieldError } from '../../../hooks';
import { RTEenterTypes } from '../../../common/enums';

interface TextWithInputProps {
  enabled: boolean | undefined;
}

const MilestoneOptionsContainer = styled.div`
  padding: ${units(2)} 7%;
`;
const FlexRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${units(1.5)};
  & > * {
    margin-right: ${units(1.5)};
  }
`;
const TextWithInput = styled.div<TextWithInputProps>`
  ${typeStyle('bodyS')};
  display: flex;
  align-items: center;
  & > * {
    margin: 0 ${units(0.5)};
  }
  ${(props) =>
    props.enabled
      ? css`
          input {
            min-width: 0;
          }
        `
      : css`
          color: ${color('lineContrast')};
          input {
            min-width: 0;
            color: ${color('lineContrast')};
          }
        `}
`;
const OptionTitle = styled.div`
  ${typeStyle('labelL')};
`;
const StyledHelpText = styled.span`
  ${typeStyle('captionM')};
`;
const StyledTimeLimitInput = styled(Input)`
  width: ${units(8)};
`;
const StyledVotingWalletInput = styled(Input)`
  width: ${units(8)};
`;

const richTextEditorConfig = {
  defaultActionOnPaste: 'insert_clear_HTML',
  buttons: ['bold', 'italic', 'ul', '|', 'undo', 'redo'],
  enter: RTEenterTypes.BR
};

export const MilestoneOptions = () => {
  const { values: challenge, setFieldValue, handleChange, handleBlur } = useFormikContext<IChallenge>();
  const { milestone, voting } = challenge;

  const isTimeLimitEnabled = Boolean(milestone?.timeLimitEnabled);

  /*
    Rich Text Editor defaultValue stored (and never updated) in state
    If defaultValue is unstable it causes rerenders on every input
  */
  const [defaultValues] = useState({
    completeMessageToAuthors: milestone?.completeMessageToAuthors || '',
    completeMessageToUsers: milestone?.completeMessageToUsers || ''
  });

  const intl = useIntl();

  const [fieldError] = useFieldError();

  return (
    <MilestoneOptionsContainer>
      <Grid>
        <GridRow>
          <GridCol>
            <FlexRow>
              <Checkbox
                label={
                  <OptionTitle>
                    {intl.formatMessage({ id: 'milestoneTimeLimit', defaultMessage: 'Set a time limit' })}
                  </OptionTitle>
                }
                checked={isTimeLimitEnabled}
                onChange={({ target: { checked } }) => {
                  const payload = checked
                    ? { ...milestone, timeLimitEnabled: true }
                    : { ...milestone, timeLimitEnabled: false, daysLimit: 0 };

                  setFieldValue('milestone', payload);
                }}
              />
              <ImportantIcon aria-hidden="true" />
              <TextWithInput enabled={isTimeLimitEnabled}>
                <FormattedMessage
                  id="milestoneTimeLimitDetail"
                  defaultMessage="Each idea will have {input} {daysCount, plural, one {day} other {days}} to reach the final milestone"
                  values={{
                    input: (
                      <StyledTimeLimitInput
                        value={milestone?.daysLimit || ''}
                        isFullWidth
                        maxLength={4}
                        name="milestone.daysLimit"
                        onChange={handleChange}
                        disabled={!isTimeLimitEnabled}
                        onBlur={handleBlur}
                      />
                    ),
                    daysCount: milestone?.daysLimit
                  }}
                />
                {fieldError('milestone.daysLimit') && <FieldStatus message="" {...fieldError('milestone.daysLimit')} />}
              </TextWithInput>
            </FlexRow>
          </GridCol>
        </GridRow>

        <GridRow>
          <GridCol>
            <FlexRow>
              <Checkbox
                label={
                  <OptionTitle>
                    {intl.formatMessage({ id: 'milestoneVotingWallet', defaultMessage: 'Enable voting Wallet' })}
                  </OptionTitle>
                }
                name="voting.wallet.isEnabled"
                checked={Boolean(voting?.wallet?.isEnabled)}
                onChange={handleChange}
              />
              <TextWithInput enabled={voting?.wallet?.isEnabled || undefined}>
                <FormattedMessage
                  id="milestoneVotingWalletDetail"
                  defaultMessage="Users will have an allowance of {input} {voteCount, plural, one {vote} other {votes}}"
                  values={{
                    input: (
                      <StyledVotingWalletInput
                        value={voting?.wallet?.allowance || ''}
                        name="voting.wallet.allowance"
                        isFullWidth
                        maxLength={4}
                        disabled={!voting?.wallet?.isEnabled}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    ),
                    voteCount: voting?.wallet?.allowance
                  }}
                />
                {fieldError('voting.wallet.allowance') && (
                  <FieldStatus message="" {...fieldError('voting.wallet.allowance')} />
                )}
              </TextWithInput>
            </FlexRow>
          </GridCol>
        </GridRow>

        <GridRow>
          <GridCol span={{ small: 12, medium: 6 }}>
            <Field
              label={intl.formatMessage({
                id: 'milestoneCompleteMessageToAuthors',
                defaultMessage: 'Message to authors when all milestones completed'
              })}
              labelVariant="emphasized"
              isFullWidth
              helpText={
                <StyledHelpText>
                  {intl.formatMessage({
                    id: 'milestoneCompleteMessageToAuthorsHelpText',
                    defaultMessage:
                      'This message will be displayed to authors above ideas that have completed all milestones'
                  })}
                </StyledHelpText>
              }
              input={
                <RichTextEditor
                  defaultValue={defaultValues.completeMessageToAuthors}
                  config={richTextEditorConfig}
                  onChange={(value) => setFieldValue('milestone.completeMessageToAuthors', value)}
                />
              }
            />
          </GridCol>

          <GridCol span={{ small: 12, medium: 6 }}>
            <Field
              label={intl.formatMessage({
                id: 'milestoneCompleteMessageToUsers',
                defaultMessage: 'Message to users when all milestones completed'
              })}
              labelVariant="emphasized"
              isFullWidth
              helpText={
                <StyledHelpText>
                  {intl.formatMessage({
                    id: 'milestoneCompleteMessageToUsersHelpText',
                    defaultMessage:
                      'This message will be displayed to users above ideas that have completed all milestones'
                  })}
                </StyledHelpText>
              }
              input={
                <RichTextEditor
                  defaultValue={defaultValues.completeMessageToUsers}
                  config={richTextEditorConfig}
                  onChange={(value) => setFieldValue('milestone.completeMessageToUsers', value)}
                />
              }
            />
          </GridCol>
        </GridRow>
      </Grid>
    </MilestoneOptionsContainer>
  );
};
